/**
 * ContentEditor.tsx
 *
 * Generic content editing component that works with both submissions and titles
 * via the adapter pattern.
 */

/* eslint-disable max-lines */

import { Fragment, useEffect, useRef, useState, type ReactElement } from "react";
import { useNavigate } from "react-router-dom";
import { GlowingEffect } from "../../shared/components/effects/GlowingEffect";
import { Button } from "../../shared/components/ui/button";
import { Input } from "../../shared/components/ui/input";
import { Label } from "../../shared/components/ui/label";
import { SPACING } from "../../shared/constants/spacing";
import { useToast } from "../../shared/hooks/use-toast";
import { normalizeGenreArray } from "../../utils/genreMapping";
import { TMDBSearchWidget } from "./components/TMDBSearchWidget";
import { useAutoSave } from "./hooks/useAutoSave";
import type { ContentAdapter, ContentFormData, SubtitleMetadata } from "./hooks/useContentEditor";
import type { CrewMember } from "./hooks/useSubmissionMutations";
import type { TitleData } from "./hooks/useTitleMutations";
import { ContentDetailsStep } from "./pages/ContentDetailsStep/ContentDetailsStep";
import { CreditsStep } from "./pages/CreditsStep/CreditsStep";
import { MediaStep } from "./pages/MediaStep/MediaStep";
import { ReviewStep } from "./pages/ReviewStep/ReviewStep";
import { UploadStep } from "./pages/UploadStep/UploadStep";

interface ContentEditorProps {
  id: string;
  adapter: ContentAdapter;
  onCancel?: () => void;
  breadcrumbPath?: { label: string; to: string }[];
  titleData?: TitleData | null; // Raw title data for admin mode (includes assets with subtitles)
  onRefreshTitle?: () => Promise<void>; // Callback to refresh title data after subtitle changes
}

/**
 * Helper function to determine step background class based on progress
 */
function getStepBgClass(stepNum: number, current: number): string {
  if (stepNum === current) {
    return "bg-lumi-accent/20 border border-lumi-accent/60 text-lumi-accent font-bold";
  }
  if (stepNum < current) {
    return "bg-green-700/20 border-2 border-green-600/40 text-green-400 font-semibold";
  }
  return "bg-neutral-900 border-2 border-neutral-700 text-neutral-500";
}

/**
 * Validate submission completeness for providers
 * Returns array of missing required fields
 */
function validateSubmission(formData: Partial<ContentFormData>): string[] {
  const errors: string[] = [];

  // Required text fields
  const requiredFields: Array<[keyof ContentFormData, string]> = [
    ["title", "Title"],
    ["releaseYear", "Release Year"],
    ["runtime", "Runtime"],
    ["rating", "Content Rating"],
    ["synopsis", "Synopsis"],
    ["description", "Description"],
    ["country", "Country"],
    ["director", "Director"],
    ["posterUrl", "Poster Image"],
    ["backdropUrl", "Backdrop Image"],
    ["logoUrl", "Logo Image"],
    ["horizontalTileUrl", "Horizontal Tile Image"],
    ["verticalTileUrl", "Vertical Tile Image"],
    ["featureVideoUrl", "Feature Film Upload"],
    ["trailerVideoUrl", "Trailer Upload"],
  ];

  for (const [field, label] of requiredFields) {
    if (!formData[field]) errors.push(label);
  }

  // Required arrays (must have at least one item)
  if (!formData.genres || formData.genres.length === 0) {
    errors.push("At least one Genre");
  }
  if (!formData.cast || formData.cast.length === 0) {
    errors.push("At least one Cast Member");
  }

  // Language validation - check both language (primary) and languages (deprecated fallback)
  const hasLanguage = Array.isArray(formData.language)
    ? formData.language.length > 0
    : Boolean(formData.language) || (formData.languages && formData.languages.length > 0);
  if (!hasLanguage) errors.push("Language");

  return errors;
}

/**
 * Helper function to get step label styling
 */
function getStepLabelClass(stepNum: number, current: number): string {
  if (stepNum === current) return "text-white font-semibold";
  if (stepNum < current) return "text-green-400 font-medium";
  return "text-neutral-500";
}

/**
 * Progress Steps Component
 */
function ProgressSteps({
  currentStep,
  onStepClick,
}: Readonly<{
  currentStep: number;
  onStepClick: (step: number) => void;
}>): ReactElement {
  const steps = [
    { num: 1, label: "Info" },
    { num: 2, label: "Content" },
    { num: 3, label: "Credits" },
    { num: 4, label: "Media" },
    { num: 5, label: "Upload" },
    { num: 6, label: "Review" },
  ];

  return (
    <div className="flex items-center justify-center px-4">
      <div className="flex items-center justify-between max-w-4xl w-full">
        {steps.map((step, idx) => (
          <Fragment key={step.num}>
            <div className="flex flex-col items-center relative">
              <button
                type="button"
                onClick={() => onStepClick(step.num)}
                disabled={step.num > currentStep}
                className={`w-12 h-12 rounded-full flex items-center justify-center text-sm transition-all ${getStepBgClass(step.num, currentStep)} ${
                  step.num <= currentStep
                    ? "cursor-pointer hover:scale-110 hover:shadow-lg"
                    : "cursor-not-allowed opacity-40"
                }`}
              >
                {step.num < currentStep ? "✓" : step.num}
              </button>
              <span className={`mt-2 text-sm whitespace-nowrap ${getStepLabelClass(step.num, currentStep)}`}>
                {step.label}
              </span>
            </div>
            {idx < steps.length - 1 && (
              <div
                className={`flex-1 h-[2px] mx-2 mb-6 transition-colors ${
                  step.num < currentStep ? "bg-green-600" : "bg-neutral-800"
                }`}
              />
            )}
          </Fragment>
        ))}
      </div>
    </div>
  );
}

/**
 * Basic Info Step Component
 */
function BasicInfoStep({
  formData,
  onChange,
  onTMDBAutofill,
}: Readonly<{
  formData: Partial<ContentFormData>;
  onChange: (field: keyof ContentFormData, value: string | number) => void;
  onTMDBAutofill: (data: {
    director?: string;
    cast?: string[];
    crew?: CrewMember[];
    genres?: string[];
    runtime?: number;
    synopsis?: string;
    title?: string;
    releaseYear?: number;
    language?: string;
    country?: string;
    posterUrl?: string;
    backdropUrl?: string;
  }) => void;
}>): ReactElement {
  return (
    <div className={SPACING.forms.sectionSpacing}>
      <div>
        <h2 className="text-2xl font-bold text-white mb-2">Basic Information</h2>
        <p className="text-sm text-neutral-400">
          Enter your film's title and release year, or search TMDB to auto-fill all metadata.
        </p>
      </div>

      <div className="bg-blue-600/10 p-6 border border-blue-600/30 rounded-lg">
        <div className="flex items-center gap-2 mb-3">
          <div className="w-1 h-5 bg-blue-500 rounded-full" />
          <h3 className="text-sm font-semibold text-white">Auto-fill from TMDB</h3>
        </div>
        <p className="text-xs text-neutral-400 mb-4">
          Search The Movie Database to automatically populate title, year, synopsis, cast, crew, and images.
        </p>
        <TMDBSearchWidget onAutofill={onTMDBAutofill} currentYear={formData.releaseYear} />
      </div>

      <form className={SPACING.forms.fieldSpacing} onSubmit={(e) => e.preventDefault()}>
        <div className="space-y-2">
          <Label htmlFor="title">
            Film Title <span className="text-red-500">*</span>
          </Label>
          <Input
            type="text"
            id="title"
            placeholder="Enter the title of your film"
            value={formData.title || ""}
            onChange={(e) => onChange("title", e.target.value)}
            required
          />
        </div>

        <div className="space-y-2">
          <Label htmlFor="releaseYear">Release Year</Label>
          <Input
            type="number"
            className="[appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
            id="releaseYear"
            placeholder="2024"
            min="1900"
            max="2099"
            value={formData.releaseYear || ""}
            onChange={(e) => onChange("releaseYear", Number.parseInt(e.target.value) || 0)}
          />
        </div>
      </form>
    </div>
  );
}

/**
 * Main ContentEditor component
 */
/* eslint-disable max-lines-per-function */
export function ContentEditor({
  id,
  adapter,
  onCancel,
  titleData,
  onRefreshTitle,
}: Readonly<ContentEditorProps>): ReactElement {
  const navigate = useNavigate();
  const { toast } = useToast();
  const [currentStep, setCurrentStep] = useState(1);
  const [transitionDirection, setTransitionDirection] = useState<"forward" | "backward">("forward");
  const [formData, setFormData] = useState<Partial<ContentFormData>>({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Track if we've successfully loaded data to prevent unnecessary reloads
  const hasLoadedRef = useRef(false);
  const lastIdRef = useRef<string | null>(null);

  // Load content data on mount and when adapter provides new data
  useEffect(() => {
    async function loadContent() {
      // If we've already loaded for this ID and adapter hasn't changed meaningfully, skip
      if (hasLoadedRef.current && lastIdRef.current === id && formData.title) {
        return;
      }

      try {
        setLoading(true);
        const data = await adapter.load(id);
        if (data) {
          setFormData(data);
          hasLoadedRef.current = true;
          lastIdRef.current = id;
        }
      } catch (err) {
        setError(err instanceof Error ? err.message : "Failed to load content");
      } finally {
        setLoading(false);
      }
    }

    void loadContent();
  }, [id, adapter]);

  // Reset loaded flag when ID changes
  useEffect(() => {
    if (lastIdRef.current !== id) {
      hasLoadedRef.current = false;
    }
  }, [id]);

  // Auto-save changes every 30 seconds (silent background saves)
  const { status: autoSaveStatus, saveNow } = useAutoSave(
    formData,
    async (data) => {
      if (data.title) {
        await adapter.save(id, data);
        // Don't refetch after save - trust the optimistic update
        // The server mutation updates the backend, but we keep the UI state as-is
      }
    },
    {
      interval: 30000,
      silent: true, // Silent background saves - no UI interruption
    },
  );

  const handleFieldChange = (field: keyof ContentFormData, value: string | number | string[] | CrewMember[]) => {
    setFormData((prev) => ({
      ...prev,
      [field]: value || undefined,
    }));
  };

  const handleVideoUpload = (type: "feature" | "trailer", assetId: string, videoUrl: string) => {
    if (type === "feature") {
      setFormData((prev) => ({
        ...prev,
        featureAssetId: assetId,
        featureVideoUrl: videoUrl,
      }));
    } else {
      setFormData((prev) => ({
        ...prev,
        trailerAssetId: assetId,
        trailerVideoUrl: videoUrl,
      }));
    }
  };

  const handleSubtitleUpload = (assetKind: "feature" | "trailer", subtitles: SubtitleMetadata[]) => {
    if (assetKind === "feature") {
      setFormData((prev) => ({
        ...prev,
        featureSubtitles: subtitles,
      }));
    } else {
      setFormData((prev) => ({
        ...prev,
        trailerSubtitles: subtitles,
      }));
    }
  };

  const handleTMDBAutofill = (data: {
    director?: string;
    cast?: string[];
    crew?: CrewMember[];
    genres?: string[];
    runtime?: number;
    synopsis?: string;
    title?: string;
    releaseYear?: number;
    language?: string;
    country?: string;
    posterUrl?: string;
    backdropUrl?: string;
  }) => {
    // Normalize genres from TMDB to ensure consistent Titlecase format
    const normalizedGenres = data.genres ? normalizeGenreArray(data.genres) : undefined;

    setFormData((prev) => ({
      ...prev,
      title: data.title || prev.title,
      releaseYear: data.releaseYear || prev.releaseYear,
      runtime: data.runtime || prev.runtime,
      synopsis: data.synopsis || prev.synopsis,
      description: data.synopsis || prev.description,
      language: data.language || prev.language,
      country: data.country || prev.country,
      director: data.director || prev.director,
      cast: data.cast || prev.cast,
      crew: data.crew || prev.crew,
      genres: normalizedGenres || prev.genres,
      posterUrl: data.posterUrl || prev.posterUrl,
      backdropUrl: data.backdropUrl || prev.backdropUrl,
    }));
  };

  const handleNextAsync = async () => {
    // Save before proceeding to next step
    await saveNow();

    // Use View Transition API if available
    if ("startViewTransition" in document) {
      setTransitionDirection("forward");
      (document as Document & { startViewTransition: (callback: () => void) => void }).startViewTransition(() => {
        setCurrentStep((prev) => Math.min(prev + 1, 6));
      });
    } else {
      setCurrentStep((prev) => Math.min(prev + 1, 6));
    }
  };

  // Wrapper for void return type
  const handleNext = () => {
    void handleNextAsync();
  };

  const handleBack = () => {
    // Use View Transition API if available
    if ("startViewTransition" in document) {
      setTransitionDirection("backward");
      (document as Document & { startViewTransition: (callback: () => void) => void }).startViewTransition(() => {
        setCurrentStep((prev) => Math.max(prev - 1, 1));
      });
    } else {
      setCurrentStep((prev) => Math.max(prev - 1, 1));
    }
  };

  const handleCancel = () => {
    if (onCancel) {
      onCancel();
    } else {
      navigate(-1);
    }
  };

  const handleSubmit = async () => {
    if (!adapter.submit) return;

    setIsSubmitting(true);

    try {
      // Determine action type for user feedback
      const needsEncoding =
        adapter.mode === "title" &&
        titleData?.assets?.some(
          (asset) =>
            asset.encodingStatus === "UPLOADED" ||
            asset.encodingStatus === "PENDING" ||
            (asset.rawVideoKey && !asset.hlsUrl),
        );

      await adapter.submit(id);

      // Show success toast
      const successTitle = needsEncoding ? "Encoding Started" : "Success";
      let successDescription: string;
      if (needsEncoding) {
        successDescription = "Video encoding has been triggered. You'll receive a notification when it completes.";
      } else if (adapter.mode === "submission") {
        successDescription = "Submission created successfully";
      } else {
        successDescription = "Changes saved successfully";
      }

      toast({
        title: successTitle,
        description: successDescription,
        variant: "default",
      });

      // Navigate back on success
      navigate(-1);
    } catch (err) {
      console.error("Submit failed:", err);

      // Parse error message
      let errorMessage: string;
      if (err instanceof Error) {
        errorMessage = err.message;
      } else if (typeof err === "string") {
        errorMessage = err;
      } else {
        errorMessage = "An unexpected error occurred";
      }

      // Show error toast
      toast({
        title: "Operation Failed",
        description: errorMessage,
        variant: "destructive",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  // Wrap handleSubmit for void return type
  const onSubmit = () => {
    void handleSubmit();
  };

  // Validate submission completeness for provider mode
  const validationErrors = adapter.mode === "submission" ? validateSubmission(formData) : [];
  const canSubmit = validationErrors.length === 0;

  // Determine submit button text based on mode and video upload status
  const getSubmitButtonText = () => {
    if (adapter.mode === "title") {
      // Check if there are videos that need encoding (UPLOADED or PENDING status)
      const needsEncoding = titleData?.assets?.some(
        (asset) =>
          asset.encodingStatus === "UPLOADED" ||
          asset.encodingStatus === "PENDING" ||
          (asset.rawVideoKey && !asset.hlsUrl), // Has raw video but no HLS URL
      );

      return needsEncoding ? "Trigger Encoding" : "Done";
    }

    // For submissions, check pending: prefix in URLs
    const hasUploadedVideos =
      formData.featureVideoUrl?.startsWith("pending:") || formData.trailerVideoUrl?.startsWith("pending:");

    return hasUploadedVideos ? "Submit & Encode" : "Submit";
  };

  // Type adapters for step components
  const handleContentDetailsChange = (field: string, value: string | string[] | number) => {
    handleFieldChange(field as keyof ContentFormData, value);
  };

  const handleCreditsChange = (field: string, value: string | string[] | CrewMember[]) => {
    handleFieldChange(field as keyof ContentFormData, value);
  };

  const handleMediaChange = (field: string, value: string) => {
    handleFieldChange(field as keyof ContentFormData, value);
  };

  const handleUploadChange = (field: string, assetId: string, videoUrl: string) => {
    handleVideoUpload(field as "feature" | "trailer", assetId, videoUrl);
  };

  if (loading) {
    return (
      <div className="mx-auto py-12 text-center">
        <p className="text-neutral-400">Loading...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="mx-auto py-12 text-center space-y-4">
        <p className="text-red-400">{error}</p>
        <button
          onClick={handleCancel}
          className="inline-block px-6 py-2 bg-neutral-800 text-white rounded-lg hover:bg-neutral-700 transition-colors"
        >
          Go Back
        </button>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Reserved space for save status indicator */}
      <div className="flex justify-end min-h-[20px]">
        {autoSaveStatus === "saving" && (
          <div className="flex items-center gap-2 text-xs text-neutral-500">
            <div className="w-2 h-2 bg-lumi-accent rounded-full animate-pulse" />
            <span>Saving...</span>
          </div>
        )}
        {autoSaveStatus === "saved" && (
          <div className="flex items-center gap-2 text-xs text-green-500 animate-fade-in">
            <div className="w-2 h-2 bg-green-500 rounded-full" />
            <span>Saved</span>
          </div>
        )}
        {autoSaveStatus === "error" && (
          <div className="flex items-center gap-2 text-xs text-red-400">
            <div className="w-2 h-2 bg-red-500 rounded-full" />
            <span>Save failed</span>
          </div>
        )}
      </div>

      <div className="bg-neutral-900/10 border border-neutral-900 rounded-lg p-8 relative">
        {/* Glowing border effect */}
        <GlowingEffect disabled={false} proximity={1000} spread={50} blur={0.5} borderWidth={1} movementDuration={4} />
        <GlowingEffect disabled={false} proximity={1000} spread={80} blur={20} borderWidth={5} movementDuration={4} />

        {/* Unified Navigation Bar with Progress Steps */}
        <div className="flex items-center justify-between mb-14 relative z-10">
          {/* Left: Back/Cancel Button */}
          <div className="w-32 mb-6">
            {currentStep === 1 ? (
              <Button variant="ghost-sm" onClick={handleCancel}>
                Cancel
              </Button>
            ) : (
              <Button variant="ghost-sm" onClick={handleBack}>
                ← Back
              </Button>
            )}
          </div>

          {/* Center: Progress Steps */}
          <div className="flex-1 px-4">
            <ProgressSteps
              currentStep={currentStep}
              onStepClick={(step) => {
                if ("startViewTransition" in document) {
                  setTransitionDirection(step > currentStep ? "forward" : "backward");
                  (document as Document & { startViewTransition: (callback: () => void) => void }).startViewTransition(
                    () => {
                      setCurrentStep(step);
                    },
                  );
                } else {
                  setCurrentStep(step);
                }
              }}
            />
          </div>

          {/* Right: Save & Continue Button */}
          <div className="w-32 mb-6 flex justify-end">
            {currentStep === 6 ? (
              <div className="relative group">
                <Button variant="ghost-sm" onClick={onSubmit} disabled={!canSubmit || isSubmitting} className="text-sm">
                  {isSubmitting ? "Processing..." : getSubmitButtonText()}
                </Button>
                {!canSubmit && (
                  <div className="absolute top-full right-0 mt-2 opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-200 w-52 z-50">
                    <div className="bg-neutral-900 border border-neutral-700 rounded-lg p-3 shadow-lg">
                      <p className="text-xs font-semibold text-red-400 mb-2">Missing Required Fields:</p>
                      <ul className="text-xs text-neutral-300 space-y-1 max-h-64 overflow-y-auto">
                        {validationErrors.map((error) => (
                          <li key={error} className="flex items-start gap-1">
                            <span className="text-red-400">•</span>
                            <span>{error}</span>
                          </li>
                        ))}
                      </ul>
                    </div>
                  </div>
                )}
              </div>
            ) : (
              <Button
                variant="ghost-sm"
                onClick={handleNext}
                disabled={currentStep === 1 && !formData.title}
                className="text-sm"
              >
                Continue →
              </Button>
            )}
          </div>
        </div>

        <div className="relative z-10 step-content-transition" data-transition-direction={transitionDirection}>
          {currentStep === 1 && (
            <BasicInfoStep formData={formData} onChange={handleFieldChange} onTMDBAutofill={handleTMDBAutofill} />
          )}

          {currentStep === 2 && <ContentDetailsStep formData={formData} onChange={handleContentDetailsChange} />}

          {currentStep === 3 && <CreditsStep formData={formData} onChange={handleCreditsChange} />}

          {currentStep === 4 && (
            <MediaStep submissionId={id} formData={formData} onChange={handleMediaChange} mode={adapter.mode} />
          )}

          {currentStep === 5 && (
            <UploadStep
              submissionId={id}
              formData={formData}
              onChange={handleUploadChange}
              onSubtitleChange={handleSubtitleUpload}
              mode={adapter.mode}
              titleData={titleData}
              onRefreshTitle={onRefreshTitle}
            />
          )}

          {currentStep === 6 && (
            <ReviewStep
              formData={formData}
              submissionId={adapter.mode === "submission" ? id : null}
              titleId={adapter.mode === "title" ? id : null}
              submitting={false}
              mode={adapter.mode}
            />
          )}
        </div>
      </div>
    </div>
  );
}
